import { callApi } from 'utils/api'
import crud from './_crud'
import { socket } from 'utils/socket/chat'
import { createHeaders, getRol } from 'utils/rol'
import { CHAT_EVENTS } from 'utils/socket/chat/events'

const entity = 'chat_passenger'
const messageEntity = 'el chat de pasajero'
const messagePluralEntity = 'los chats de pasajero'

const updateChat = (chat) => {
	return async (dispatch, getState) => {
		const state = getState()

		dispatch({
			type: 'UPDATE_CHAT',
			payload: { id: chat.id, data: chat },
		})
	}
}

const getMessages = ({ groupPassengerId }) => {
	return async (dispatch, getState) => {
		const state = getState()
    
		try {
      const rol = getRol(state)
      const headers = createHeaders(state, rol)

			const response = await callApi(
				`/${entity}/utils/get_messages_by_usertype`,
				{
					method: 'GET',
					headers,
					params: {
						group_passenger_id: groupPassengerId,
					},
				}
			)

			if (response && response.body) {
				const { chatId, messages } = response.body
				dispatch({
					type: 'LOAD_MESSAGES',
					payload: { id: chatId, data: messages },
				})
				return messages
			} else {
				console.error('Response body is undefined:', response)
				return [] // Retorna un array vacío en caso de respuesta sin body
			}
		} catch (error) {
			console.error('Error fetching messages:', error)
		}
	}
}

const loadWithLastMessage = () => {
	return async (dispatch, getState) => {
		const state = getState()
    
		try {
      const rol = getRol(state)
      const headers = createHeaders(state, rol)
      
			const res = await callApi(
				`/${entity}/utils/load_chats_with_last_message_by_usertype`,
				{
					method: 'GET',
					headers,
				}
			)

			dispatch({
				type: 'LOAD_WITH_LAST_MESSAGE',
				payload: res.body,
			})

			return res.body
		} catch (error) {
			console.error('Error loading chats:', error)
		}
	}
}

const newMessageReceived = ({ chat, message }) => {
	return async (dispatch, getState) => {
		const state = getState()
    const {
      entities: chats,
      activeChatId,
    } = state.chat_passengers
    const activeChat = chats[activeChatId]
    const currentChat = chats[chat.id]
    const currentGroupId = activeChat?.group_passenger_id
    const messageGroupId = message.ChatPassenger?.group_passenger_id

    const user = state.app.user
    const client = state.app.client
    
		let lastMessage = message
    
    // Check for temporary chat replacement (Optimistic Update -> Real Chat)
    const tempChat = Object.values(chats).find(
      (c) => c.isTemp && c.group_passenger_id === messageGroupId
    )
    if (tempChat && chat.id !== tempChat.id) {
      // If we were viewing the temp chat, switch to the real one
      if (activeChatId === tempChat.id) {
        dispatch(setActiveChatId(chat.id))
      }
      
      // Remove the temporary chat from the store to prevent duplicates
      dispatch({
        type: 'DELETE_CHAT_PASSENGER',
        payload: tempChat.id
      })
    }

    // Check if we already have this message (optimistic update confirmation)
    if (message.tempId && currentChat?.Messages) {
      const existingMessageIndex = currentChat.Messages.findIndex(m => m.tempId === message.tempId)
      if (existingMessageIndex !== -1) {
        // Replace the temp message with the real one
        const updatedMessages = [...currentChat.Messages]
        updatedMessages[existingMessageIndex] = lastMessage
        
        dispatch({
          type: 'UPDATE_MESSAGES',
          payload: {
            id: chat.id,
            data: updatedMessages,
          },
        })
        return
      }
    }

    const isMessageFromUser = 
			(user && user.id === message.user_id) || 
			(client && client.id === message.client_contact_id)
			
		dispatch({
			type: 'SET_NEW_MESSAGE',
			payload: {
				id: chat.id,
				data: { chat, lastMessage },
        isOwnMessage: isMessageFromUser
			},
		})
	}
}

const sendMessage = (message) => {
  return async (dispatch, getState) => {
    const state = getState()
    const { groupId, content } = message
    const { entities: chats } = state.chat_passengers
    
    // Find chat by groupId
    let chat = Object.values(chats).find(c => c.group_passenger_id === groupId)
    
    const tempId = `temp_${Date.now()}`
    
    // If chat doesn't exist (New Chat), create a temporary one for optimistic update
    if (!chat) {
       const group = state.group_passengers?.entities?.[groupId] || { id: groupId, name: 'Nuevo Chat' }
       chat = {
         id: `temp_chat_${Date.now()}`,
         group_passenger_id: groupId,
         GroupPassenger: group,
         Messages: [],
         isTemp: true, // Flag to identify temp chat
         hasMessages: true // Ensure it shows up in filtered lists
       }
    }
    
    if (chat) {
      const user = state.app.user
      const client = state.app.client

      const messageObj = {
        id: tempId,
        chat_id: chat.id,
        content,
        user_id: user?.id || null,
        client_contact_id: client?.id || null,
        passenger_id: null,
        User: user ? {
          id: user?.id,
          name: user?.name,
        } : null,
        ClientContact: client ? {
          id: client?.id,
          name: client?.name,
        } : null,
        createdAt: new Date().toISOString(),
        ChatPassenger: {
          group_passenger_id: groupId
        },
        read_by: [],
        tempId, // Marker for optimistic update
      }

      // Dispatch optimistic update
      dispatch(newMessageReceived({ chat, message: messageObj }))
    }

    // Emit socket event with tempId
    socket.emit(CHAT_EVENTS.SEND_MESSAGE, { ...message, tempId })
  }
}

const updateMessages = ({ chatId, messages }) => {
	return async (dispatch, getState) => {
		const state = getState()
		dispatch({
			type: 'UPDATE_MESSAGES',
			payload: {
				id: chatId,
				data: messages,
				userId: state.app.user?.id,
				clientContactId: state.app.client?.id,
			},
		})
	}
}

const setPinnedMessage = ({ message, groupId }) => {
	return async (dispatch, getState) => {
		const state = getState()

		const res = await callApi(`/${entity}/utils/set_pinned_message`, {
			method: 'PATCH',
			body: JSON.stringify({
				pinned_message: message,
				group_passenger_id: groupId,
			}),
			headers: {
				Authorization: state.app.user.token,
			},
			// successMessage: `Se actualizó el estado de silencio correctamente.`,
		})

		dispatch({
			type: `UPDATE_${entity.toUpperCase()}`,
			response: res.body,
		})

		return res.body
	}
}

const setUnreadMessages = (unreadMessages) => {
  return async (dispatch, getState) => {
    const state = getState()

    dispatch({
      type: 'SET_UNREAD_MESSAGES',
      payload: unreadMessages,
    })
  }
}

const setActiveChatId = (id) => {
  return async (dispatch, getState) => {
    const state = getState()
    const { activeChatId } = state.chat_passengers
    console.log('setActiveChatId', id, activeChatId)

    if (activeChatId === id) return
    
    dispatch({
      type: 'SET_ACTIVE_CHAT_ID',
      payload: id
    })
  }
}

const emitSocketEvent = (event, payload) => {
  return async (dispatch, getState) => {
    const state = getState()
    socket.emit(event, payload)
  }
}

const markMessagesAsRead = ({ groupId, userId, clientContactId }) => {
  return async (dispatch, getState) => {
    const state = getState()
    const { entities: chats } = state.chat_passengers
    
    const chat = Object.values(chats).find(c => c.group_passenger_id === groupId)
    
    if (chat) {
      dispatch({
        type: 'MARK_AS_READ',
        payload: {
          chatId: chat.id,
          userId,
          clientContactId
        }
      })
    }

    socket.emit(CHAT_EVENTS.READ_MESSAGES, {
      groupId,
      userId,
      clientContactId
    })
  }
}


const actions = crud(entity, messageEntity, messagePluralEntity)

export default {
	...actions,
	updateChat,
	loadWithLastMessage,
	getMessages,
	newMessageReceived,
	updateMessages,
	setPinnedMessage,
  setUnreadMessages,
  setActiveChatId,
  emitSocketEvent,
  sendMessage,
  markMessagesAsRead,
}